/* import __COLOCATED_TEMPLATE__ from './ip-address-modal.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
import IpAllowListValidator from 'embercom/lib/ip-allowlist-validator';

interface Arguments {
  onClose: () => void;
  showModal: boolean;
  ipAddresses: string[] | undefined;
  onIpAddressAdded: (ipAddress: string) => void;
}

interface CidrPrefixOption {
  value: string;
  text: string;
}

export default class IpAddressModal extends Component<Arguments> {
  @service declare intl: IntlService;
  @tracked ipAddressInput = '';
  @tracked selectedCidrPrefix = '/32';
  @tracked ipAddressIsValid = false;

  get cidrPrefixOptions(): CidrPrefixOption[] {
    return [
      { value: '/32', text: '/32' },
      { value: '/24', text: '/24' },
      { value: '/16', text: '/16' },
      { value: '/8', text: '/8' },
    ];
  }

  get fullIpAddress(): string {
    if (!this.ipAddressInput) {
      return '';
    }
    // Default to /32 if no prefix is selected
    let prefix = this.selectedCidrPrefix || '/32';
    return `${this.ipAddressInput}${prefix}`;
  }

  @action
  handleModalClose() {
    this.args.onClose();
    this.resetAttributes();
  }

  @action
  saveIpAddress() {
    this.args.onIpAddressAdded(this.fullIpAddress);
    this.handleModalClose();
  }

  @action
  cancelAddIpAddress() {
    this.handleModalClose();
  }

  @action
  resetAttributes() {
    this.ipAddressInput = '';
    this.selectedCidrPrefix = '/32';
    this.ipAddressIsValid = false;
  }

  @action
  validateIpAddress() {
    // Parse CIDR notation if typed directly in input (e.g., "192.168.1.1/32")
    let inputValue = this.ipAddressInput;
    let cidrMatch = inputValue.match(/^(.+)\/(\d+)$/);

    if (cidrMatch) {
      let [, ipPart, prefixPart] = cidrMatch;
      this.ipAddressInput = ipPart.trim();
      let prefixValue = `/${prefixPart}`;
      // Only set dropdown if it's one of our predefined options
      let matchingOption = this.cidrPrefixOptions.find((opt) => opt.value === prefixValue);
      if (matchingOption) {
        this.selectedCidrPrefix = prefixValue;
      } else {
        // If it's a valid CIDR but not in dropdown, default to /32
        // The full address will still be validated correctly
        this.selectedCidrPrefix = '/32';
      }
    }

    let fullAddress = this.fullIpAddress;
    let ipValidator = new IpAllowListValidator(fullAddress, this.intl);
    this.ipAddressIsValid = ipValidator.isValid;
  }

  @action
  onCidrPrefixSelected(value: string) {
    this.selectedCidrPrefix = value;
    this.validateIpAddress();
  }

  get hintText(): string {
    let prefix = this.selectedCidrPrefix || '/32';
    let hintKey = `ai-agent.voice-playground.ip-address-modal.hint-${prefix.replace('/', '')}`;
    // Fallback to default hint if specific hint doesn't exist
    return this.intl.exists(hintKey)
      ? this.intl.t(hintKey)
      : this.intl.t('ai-agent.voice-playground.ip-address-modal.hint-default');
  }

  @action
  getIpAddressErrorMessage() {
    return this.intl.t('ai-agent.voice-playground.ip-address-modal.error-message');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Deploy::Voice::IpAddressModal': typeof IpAddressModal;
  }
}
